import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { useEffect, useState } from 'react';
import { useAuth } from '../components/AuthGate';
import { NotificationsApi, Notification } from '../api/notifications';

type HealthState = { status: string; db: string } | null;

function StatCard({
  icon,
  label,
  value,
  color = 'text.primary',
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color?: string;
}) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 160 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Box>
          <Typography variant="h5" fontWeight={700} color={color}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { token } = useAuth();
  const [health, setHealth] = useState<HealthState>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [recent, setRecent] = useState<Notification[]>([]);
  const [totalUnread, setTotalUnread] = useState<number | null>(null);
  const [totalAll, setTotalAll] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    NotificationsApi.health()
      .then(setHealth)
      .catch(() => setHealth({ status: 'error', db: 'unreachable' }))
      .finally(() => setHealthLoading(false));
  }, []);

  // Use a sample recipientId to fetch recent/unread — real dashboard would aggregate.
  // For now we fetch the last 5 across a broad query context (requires recipientId per API design).
  // We surface counts from the pagination.count field.
  useEffect(() => {
    // Fetch using a wildcard-like broad recipientId isn't supported by the API (by design).
    // So the dashboard shows counts for a configurable "default" view and the last created notifications.
    // Users can use the Browse page for full filtering.
    setError('');
    const dummyId = '__dashboard__';
    Promise.allSettled([
      NotificationsApi.list({ recipientId: dummyId, status: 'unread', limit: 1, offset: 0 }, token),
      NotificationsApi.list({ recipientId: dummyId, limit: 1, offset: 0 }, token),
    ]).then(([unreadResult, allResult]) => {
      if (unreadResult.status === 'fulfilled') setTotalUnread(unreadResult.value.pagination.count);
      if (allResult.status === 'fulfilled') {
        setTotalAll(allResult.value.pagination.count);
        setRecent(allResult.value.data.slice(0, 5));
      }
    });
  }, [token]);

  return (
    <Box>
      {/* Health */}
      <Typography variant="subtitle2" color="text.secondary" mb={1}>
        Service health
      </Typography>
      {healthLoading ? (
        <CircularProgress size={20} />
      ) : health ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          {health.status === 'ok' ? (
            <CheckCircleOutlineIcon color="success" fontSize="small" />
          ) : (
            <ErrorOutlineIcon color="error" fontSize="small" />
          )}
          <Typography variant="body2">
            API: <strong>{health.status}</strong> &nbsp;·&nbsp; DB:{' '}
            <strong>{health.db}</strong>
          </Typography>
        </Box>
      ) : null}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        <StatCard
          icon={<NotificationsNoneIcon />}
          label="Total (this view)"
          value={totalAll ?? '—'}
        />
        <StatCard
          icon={<MarkEmailReadIcon />}
          label="Unread (this view)"
          value={totalUnread ?? '—'}
          color="error.main"
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Recent notifications */}
      <Typography variant="subtitle2" color="text.secondary" mb={1}>
        Use the <strong>Browse</strong> page to search and filter notifications by recipient, source
        app, and status.
      </Typography>

      {recent.length > 0 && (
        <>
          <Typography variant="subtitle2" mt={2} mb={1}>
            Recent (this view)
          </Typography>
          <List dense disablePadding>
            {recent.map((n) => (
              <ListItem key={n.id} divider sx={{ py: 0.75 }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={n.status === 'unread' ? 600 : 400}>
                      {n.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {n.recipientId} · {n.status}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );
}
